import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  aiPodBillingService,
  formatMoney,
  type AiPodPlanDto,
  type FeatureMeta,
  type PlanWithFeatures,
  type UsageMeterDto,
  type PlanMeterRow,
} from '../../../../services/aiPodBillingService';
import { featureService } from '../../../../services/featureService';
import { PlanModal, type PlanItem, type PlanFeatureValueRow } from './PlanModal';
import { Badge } from '../../../../components/ui/Badge';
import { Button, Spinner } from '../../../../components/ui/Button';
import { SelectField, TextField } from '../../../../components/ui/Field';
import { Surface } from '../../../../components/ui/Surface';
import { cssColor } from '../../../../theme/engine';

type Props = { aiPodId: string };

const INTERVAL_SHORT: Record<string, string> = { month: 'Monthly', year: 'Yearly' };

// Usage meters, overage policies and margins are parked for now: plan limits are set as
// plan features (voice call minutes, messages) and enforced from those. Flip to bring the
// meter configuration back.
const METERS_ENABLED = false;

const METER_KEY_OPTIONS = [
  { value: 'pod_run', label: 'Pod runs', unit: 'run' },
  { value: 'outbound_call', label: 'Outbound calls', unit: 'call' },
  { value: 'call_minute', label: 'Call minutes', unit: 'minute' },
  { value: 'conversation', label: 'Conversations', unit: 'conversation' },
];

const OVERAGE_POLICIES = [
  {
    value: 'ALLOW',
    label: 'Charge and notify',
    description: 'Bill overage, email at 80% and 100%',
  },
  {
    value: 'HARD_STOP',
    label: 'Pause the Pod',
    description: 'Stop until next cycle or upgrade',
  },
  {
    value: 'NOTIFY_ONLY',
    label: 'Notify only',
    description: 'Absorb the cost, nudge to upgrade',
  },
];

function displayValue(feature: FeatureMeta, value: any): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-agentic-text-muted text-sm">—</span>;
  }
  if (feature.type === 'boolean') {
    const on = value === true || value === 'true';
    return (
      <div className={`h-[22px] w-[22px] rounded-[4px] flex items-center justify-center shrink-0 border ${on ? 'bg-kv-agentic-blue border-kv-agentic-blue' : 'bg-white border-gray-300'}`}>
        {on && (
          <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    );
  }
  if (feature.type === 'limit') {
    return (
      <div className="min-w-[72px] rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm text-gray-800 text-right tabular-nums font-medium">
        {value === -1 ? 'Unlimited' : String(value)}
      </div>
    );
  }
  const opt = feature.type === 'option'
    ? (feature.options ?? []).find((o: any) => o.value === value)
    : null;
  return (
    <div className="min-w-[72px] rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm text-gray-800 font-medium">
      {opt?.label ?? String(value)}
    </div>
  );
}

export const PodPlansGrid: React.FC<Props> = ({ aiPodId }) => {
  const [plans, setPlans] = useState<AiPodPlanDto[]>([]);
  const [matrixPlans, setMatrixPlans] = useState<PlanWithFeatures[]>([]);
  const [featureMap, setFeatureMap] = useState<Record<string, FeatureMeta>>({});
  const [allFeatures, setAllFeatures] = useState<FeatureMeta[]>([]);
  const [loading, setLoading] = useState(true);

  const [meters, setMeters] = useState<UsageMeterDto[]>([]);
  const [planMetersMap, setPlanMetersMap] = useState<Record<string, PlanMeterRow[]>>({});
  const [localMeterInputs, setLocalMeterInputs] = useState<Record<string, string>>({});

  // meter editor state — primary
  const [meterKey, setMeterKey] = useState('pod_run');
  const [meterLabel, setMeterLabel] = useState(METER_KEY_OPTIONS[0].label);
  const [meterCost, setMeterCost] = useState('0.02');
  const [meterPolicy, setMeterPolicy] = useState<'ALLOW' | 'HARD_STOP' | 'NOTIFY_ONLY'>('ALLOW');
  // meter editor state — secondary (optional, blank by default)
  const [meter2Key, setMeter2Key] = useState(METER_KEY_OPTIONS[1]?.value ?? 'outbound_call');
  const [meter2Label, setMeter2Label] = useState('');
  const [meter2Cost, setMeter2Cost] = useState('0.02');
  const [savingMeter, setSavingMeter] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<AiPodPlanDto | null>(null);
  const [editingItems, setEditingItems] = useState<PlanItem[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, matrixRes, featuresRes, metersRes, planMetersRes] = await Promise.all([
        aiPodBillingService.listBillingPlans(aiPodId, { includeArchived: true }),
        aiPodBillingService.getPlanFeatures(aiPodId),
        featureService.list({ page_size: 100 }),
        aiPodBillingService.listUsageMeters(aiPodId),
        aiPodBillingService.getPlanMeters(aiPodId),
      ]);
      // Cheapest first, so "Recommended" (the second card) is the step-up plan.
      setPlans(([...plansRes.plans] as any as AiPodPlanDto[]).sort((a, b) => a.amountMinor - b.amountMinor));
      setMatrixPlans(matrixRes.plans);
      const fMap: Record<string, FeatureMeta> = {};
      for (const f of matrixRes.features) fMap[f.id] = f;
      setFeatureMap(fMap);
      setAllFeatures((featuresRes.features ?? []) as any as FeatureMeta[]);
      setMeters(metersRes.meters);
      if (metersRes.meters.length > 0) {
        const m = metersRes.meters[0];
        setMeterKey(m.key);
        setMeterLabel(m.label);
        setMeterCost((m.internalCostMinor / 100).toString());
        setMeterPolicy((m.overagePolicy as any) ?? 'ALLOW');
      }
      if (metersRes.meters.length > 1) {
        const m2 = metersRes.meters[1];
        setMeter2Key(m2.key);
        setMeter2Label(m2.label);
        setMeter2Cost((m2.internalCostMinor / 100).toString());
      } else {
        // Reset secondary so stale state from another pod doesn't leak through
        setMeter2Key(METER_KEY_OPTIONS[1]?.value ?? 'outbound_call');
        setMeter2Label('');
        setMeter2Cost('0.02');
      }
      const pmMap: Record<string, PlanMeterRow[]> = {};
      const localInputs: Record<string, string> = {};
      for (const p of planMetersRes.plans) {
        pmMap[p.id] = p.planMeters;
        for (const pm of p.planMeters) {
          localInputs[`${p.id}-${pm.meterId}-includedQty`] = String(pm.includedQty);
          localInputs[`${p.id}-${pm.meterId}-overageMinor`] = (pm.overageMinor / 100).toFixed(2);
        }
      }
      setPlanMetersMap(pmMap);
      setLocalMeterInputs(localInputs);
    } catch {
      toast.error('Failed to load plans data');
    } finally {
      setLoading(false);
    }
  }, [aiPodId]);

  useEffect(() => { loadData(); }, [loadData]);

  // A plan's features with their value for each billing interval (values are stored per interval).
  const getPlanItems = (planId: string): PlanItem[] => {
    const mp = matrixPlans.find((p) => p.id === planId);
    if (!mp) return [];
    const byFeature = new Map<string, PlanItem>();
    for (const pf of mp.planFeatures) {
      const feature = featureMap[pf.featureId];
      if (!feature) continue;
      const item = byFeature.get(pf.featureId) ?? { featureId: pf.featureId, feature, values: {} };
      item.values[pf.interval] = pf.value;
      byFeature.set(pf.featureId, item);
    }
    return [...byFeature.values()];
  };

  const saveMeter = async () => {
    const costMinor = Math.round(parseFloat(meterCost) * 100);
    if (!meterLabel || isNaN(costMinor)) return toast.error('Fill in all meter fields');
    setSavingMeter(true);
    try {
      // If the user changed the primary meter key, delete the old one first
      if (meters[0] && meters[0].key !== meterKey) {
        await aiPodBillingService.deleteUsageMeter(aiPodId, meters[0].id);
      }
      const opt = METER_KEY_OPTIONS.find((o) => o.value === meterKey);
      await aiPodBillingService.upsertUsageMeter(aiPodId, {
        key: meterKey,
        label: meterLabel,
        unit: opt?.unit ?? meterKey,
        internalCostMinor: costMinor,
        displayOrder: 0,
        overagePolicy: meterPolicy,
      });
      if (meter2Label.trim()) {
        // If the user changed the secondary meter key, delete the old one first
        if (meters[1] && meters[1].key !== meter2Key) {
          await aiPodBillingService.deleteUsageMeter(aiPodId, meters[1].id);
        }
        const opt2 = METER_KEY_OPTIONS.find((o) => o.value === meter2Key);
        await aiPodBillingService.upsertUsageMeter(aiPodId, {
          key: meter2Key,
          label: meter2Label,
          unit: opt2?.unit ?? meter2Key,
          internalCostMinor: Math.round(parseFloat(meter2Cost) * 100) || 0,
          displayOrder: 1,
          overagePolicy: 'ALLOW',
        });
      } else if (meters[1]) {
        await aiPodBillingService.deleteUsageMeter(aiPodId, meters[1].id);
      }
      toast.success('Meters saved');
      await loadData();
    } catch {
      toast.error('Failed to save meter');
    } finally {
      setSavingMeter(false);
    }
  };

  const deleteMeter = async (meterId: string) => {
    try {
      await aiPodBillingService.deleteUsageMeter(aiPodId, meterId);
      setMeters([]);
      setPlanMetersMap({});
      setLocalMeterInputs({});
      setMeterKey('pod_run');
      setMeterLabel(METER_KEY_OPTIONS[0].label);
      setMeterCost('0.02');
      setMeterPolicy('ALLOW');
      setMeter2Key(METER_KEY_OPTIONS[1]?.value ?? 'outbound_call');
      setMeter2Label('');
      setMeter2Cost('0.02');
      toast.success('Meter removed');
    } catch {
      toast.error('Failed to remove meter');
    }
  };

  const updatePlanMeter = async (planId: string, meterId: string, field: 'includedQty' | 'overageMinor', raw: string) => {
    if (meterId === '__unsaved__') { toast.info('Save the meter first, then set per-plan quantities.'); return; }
    const val = field === 'overageMinor' ? Math.round(parseFloat(raw) * 100) : parseInt(raw, 10);
    if (isNaN(val)) return;
    const existing = planMetersMap[planId] ?? [];
    const row = existing.find((r) => r.meterId === meterId);
    const rows = row
      ? existing.map((r) => r.meterId === meterId ? { ...r, [field]: val } : r)
      : [...existing, { id: '', meterId, includedQty: field === 'includedQty' ? val : 0, overageMinor: field === 'overageMinor' ? val : 0 }];
    try {
      await aiPodBillingService.replacePlanMeters(aiPodId, planId, rows.map((r) => ({ meterId: r.meterId, includedQty: r.includedQty, overageMinor: r.overageMinor })));
      setPlanMetersMap((prev) => ({ ...prev, [planId]: rows as PlanMeterRow[] }));
    } catch {
      toast.error('Failed to save meter value');
    }
  };

  const openCreate = () => { setEditingPlan(null); setEditingItems([]); setShowModal(true); };

  const openEdit = (plan: AiPodPlanDto) => {
    setEditingPlan(plan);
    setEditingItems(getPlanItems(plan.id));
    setShowModal(true);
  };

  const handleSave = async (payload: any, rows: PlanFeatureValueRow[]) => {
    let planId: string;
    if (editingPlan) {
      await aiPodBillingService.updatePlan(aiPodId, editingPlan.id, {
        displayName: payload.displayName,
        description: payload.description,
        pricingIntervals: payload.pricingIntervals,
        trialDays: payload.trialDays,
      });
      planId = editingPlan.id;
      toast.success('Plan updated');
    } else {
      const created = await aiPodBillingService.createPlan(aiPodId, payload);
      planId = created.id;
      toast.success('Plan created');
    }
    await aiPodBillingService.replacePlanFeatures(aiPodId, planId, rows);
    await loadData();
  };

  const toggleArchived = async (plan: AiPodPlanDto) => {
    const archiving = plan.isActive !== false;
    try {
      await aiPodBillingService.updatePlan(aiPodId, plan.id, { isActive: !archiving });
      toast.success(archiving ? 'Plan archived — existing subscribers keep it' : 'Plan restored');
      await loadData();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to update plan');
    }
  };

  const handleDelete = async (plan: AiPodPlanDto) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete plan "${plan.displayName}"? This cannot be undone.`)) return;
    try {
      await aiPodBillingService.deletePlan(aiPodId, plan.id);
      toast.success('Plan deleted');
      await loadData();
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to delete plan');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner as="div" className="h-6 w-6 animate-spin rounded-full border-2 border-kv-agentic-blue border-t-transparent" />
      </div>
    );
  }

  // Use saved DB meter if available; otherwise synthesize from form state so
  // the included-qty / overage rows always appear on the plan cards.
  const savedMeter = meters[0] ?? null;
  const opt = METER_KEY_OPTIONS.find((o) => o.value === meterKey);
  const effectiveMeter: UsageMeterDto = savedMeter ?? {
    id: '__unsaved__',
    aiPodId,
    key: meterKey,
    label: meterLabel || (opt?.label ?? meterKey),
    unit: opt?.unit ?? meterKey,
    internalCostMinor: Math.round(parseFloat(meterCost || '0') * 100) || 0,
    overagePolicy: meterPolicy,
    displayOrder: 0,
  };
  const meter = effectiveMeter;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-4 pb-3 shrink-0">
        <div>
          <h3 className="text-sm font-semibold text-agentic-text-primary">Billing Plans</h3>
          <p className="text-xs text-agentic-text-muted mt-0.5">{plans.length} plan{plans.length !== 1 ? 's' : ''}</p>
        </div>
        <Button tone="primary-gradient"
          onClick={openCreate}
          className="rounded-xl px-3 py-1.5 text-xs font-medium transition-all"
        >
          + New Plan
        </Button>
      </div>

      {/* Usage Meter Config (parked — see METERS_ENABLED) */}
      {METERS_ENABLED && (
      <div className="mx-6 mb-3 rounded-xl border border-agentic-gray-200 bg-white overflow-hidden">
        {/* Title row */}
        <div className="flex items-start justify-between px-4 pt-4 pb-3">
          <p className="text-sm font-semibold text-agentic-text-primary">Usage meters</p>
          <p className="text-xs text-agentic-text-muted text-right max-w-[220px]">Up to 2 per Pod.  The primary meter leads on the pricing page.</p>
        </div>

        {/* Primary row */}
        <div className="flex items-end gap-3 px-4 pb-3">
          <div className="shrink-0 pb-[9px]">
            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-blue-200">Primary</span>
          </div>
          <div className="grid grid-cols-3 gap-3 flex-1">
            <div>
              <label className="block text-xs text-agentic-text-muted mb-1">Meter</label>
              <SelectField tone="agentic-focus-brand" value={meterKey}
                onChange={(e) => { setMeterKey(e.target.value); setMeterLabel(METER_KEY_OPTIONS.find(o => o.value === e.target.value)?.label ?? ''); }}
                className="w-full rounded-lg border border-agentic-gray-300 bg-white px-2 py-2 text-sm text-agentic-text-primary focus:outline-none">
                {METER_KEY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </SelectField>
            </div>
            <div>
              <label className="block text-xs text-agentic-text-muted mb-1">Customer label</label>
              <TextField tone="agentic-focus-brand" value={meterLabel}
                onChange={(e) => setMeterLabel(e.target.value)}
                placeholder={METER_KEY_OPTIONS.find(o => o.value === meterKey)?.label ?? ''}
                className="w-full rounded-lg border border-agentic-gray-300 px-3 py-2 text-sm focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-agentic-text-muted mb-1">
                Your cost per {METER_KEY_OPTIONS.find(o => o.value === meterKey)?.unit ?? meterKey}
              </label>
              <div className="flex items-center rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 gap-1">
                <span className="text-sm text-agentic-text-muted">$</span>
                <TextField tone="agentic-focus-brand" type="number" step="0.001" min="0"
                  value={meterCost} onChange={(e) => setMeterCost(e.target.value)}
                  placeholder="0.02"
                  className="flex-1 text-sm focus:outline-none bg-transparent" />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary row */}
        <div className="flex items-end gap-3 px-4 pb-4">
          <div className="shrink-0 pb-[9px]">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600 ring-1 ring-gray-200">Secondary</span>
          </div>
          <div className="grid grid-cols-3 gap-3 flex-1">
            <div>
              <label className="block text-xs text-agentic-text-muted mb-1">Meter</label>
              <SelectField tone="agentic-focus-brand" value={meter2Key}
                onChange={(e) => { setMeter2Key(e.target.value); setMeter2Label(METER_KEY_OPTIONS.find(o => o.value === e.target.value)?.label ?? ''); }}
                className="w-full rounded-lg border border-agentic-gray-300 bg-white px-2 py-2 text-sm text-agentic-text-primary focus:outline-none">
                {METER_KEY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </SelectField>
            </div>
            <div>
              <label className="block text-xs text-agentic-text-muted mb-1">Customer label</label>
              <TextField tone="agentic-focus-brand" value={meter2Label}
                onChange={(e) => setMeter2Label(e.target.value)}
                placeholder={METER_KEY_OPTIONS.find(o => o.value === meter2Key)?.label ?? ''}
                className="w-full rounded-lg border border-agentic-gray-300 px-3 py-2 text-sm focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-agentic-text-muted mb-1">
                Your cost per {METER_KEY_OPTIONS.find(o => o.value === meter2Key)?.unit ?? meter2Key}
              </label>
              <div className="flex items-center rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 gap-1">
                <span className="text-sm text-agentic-text-muted">$</span>
                <TextField tone="agentic-focus-brand" type="number" step="0.001" min="0"
                  value={meter2Cost} onChange={(e) => setMeter2Cost(e.target.value)}
                  placeholder="0.02"
                  className="flex-1 text-sm focus:outline-none bg-transparent" />
              </div>
            </div>
          </div>
        </div>

        {/* Info note */}
        <div className="px-4 py-3 border-t border-agentic-gray-100">
          <p className="text-xs text-agentic-text-muted">
            Costs are internal only. <strong className="text-agentic-text-primary font-semibold">Minimum margin: 60%</strong> at full included usage of both meters, from Pricing rules. Yearly discount, trial and Pod Pack discounts apply automatically.
          </p>
        </div>

        {/* Save row */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-agentic-gray-100">
          <Button tone="primary-gradient" onClick={saveMeter} disabled={savingMeter}
            className="rounded-xl px-4 py-2 text-xs font-medium transition-all disabled:opacity-60">
            {savingMeter ? 'Saving…' : meters.length > 0 ? 'Update meters' : 'Save meters'}
          </Button>
        </div>
      </div>
      )}

      <div className="px-6 pb-6">
        {plans.length === 0 ? (
          <Surface tone="card-agentic" className="rounded-2xl border py-16 text-center">
            <p className="text-sm font-medium text-agentic-text-secondary">No plans yet</p>
            <p className="mt-1 text-xs text-agentic-text-muted">Create your first billing plan to get started.</p>
          </Surface>
        ) : (
          <>
            {/* Plan cards grid */}
            <div className={`grid gap-4 items-stretch mb-5 ${plans.length === 1 ? 'grid-cols-1 max-w-xs' : plans.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {plans.map((plan, idx) => {
                const items = getPlanItems(plan.id);
                const isRecommended = idx === 1 && plans.length >= 2;
                const pm = meter ? (planMetersMap[plan.id] ?? []).find((r) => r.meterId === meter.id) : null;
                const includedQty = pm?.includedQty ?? 0;
                const overageMinor = pm?.overageMinor ?? 0;
                const costAtFull = meter ? (includedQty * meter.internalCostMinor) / 100 : 0;
                // Use monthly price for margin calculation; fall back to amountMinor for single-interval plans
                const intervals = (plan as any).pricingIntervals as Array<{ interval: string; amountMinor: number }> | undefined;
                const monthlyInterval = intervals?.find((pi) => pi.interval === 'month');
                const planPrice = (monthlyInterval?.amountMinor ?? plan.amountMinor) / 100;
                const margin = planPrice > 0 ? Math.round(((planPrice - costAtFull) / planPrice) * 100) : null;
                const overageMargin = overageMinor > 0 && meter
                  ? Math.round(((overageMinor - meter.internalCostMinor) / overageMinor) * 100)
                  : null;
                const marginColor = margin === null ? 'text-gray-400'
                  : margin >= 70 ? 'text-emerald-600'
                  : margin >= 60 ? 'text-amber-500'
                  : 'text-red-500';
                const marginLabel = margin === null ? '—'
                  : margin >= 70 ? 'Healthy'
                  : margin >= 60 ? 'At minimum'
                  : 'Below minimum';

                return (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col rounded-2xl bg-white border-2 ${
                      isRecommended
                        ? 'border-kv-agentic-blue shadow-lg shadow-blue-100'
                        : 'border-gray-200'
                    }`}
                  >
                    {/* Fixed header: plan name + price + meter rows */}
                    <div className="px-5 pt-5 pb-4 shrink-0">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <h4 className="text-base font-bold text-gray-900">{plan.displayName}</h4>
                        {isRecommended && (
                          <Badge className="shrink-0 rounded-full bg-kv-agentic-blue px-2.5 py-0.5 text-xs font-semibold text-white">
                            Recommended
                          </Badge>
                        )}
                      </div>

                      {plan.isActive === false && (
                        <Badge className="mb-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
                          Archived — hidden from new subscribers
                        </Badge>
                      )}

                      {/* Price rows */}
                      {((plan as any).pricingIntervals?.length > 0
                        ? (plan as any).pricingIntervals
                        : [{ interval: plan.interval ?? 'month', amountMinor: plan.amountMinor, currency: plan.currency }]
                      ).map((pi: any) => (
                        <div key={pi.interval} className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-500">Price per {pi.interval}</span>
                          <span className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm font-semibold text-gray-900 tabular-nums">
                            {pi.amountMinor === 0 ? 'Free' : formatMoney(pi.amountMinor, pi.currency)}
                          </span>
                        </div>
                      ))}
                      {(plan.trialDays ?? 0) > 0 && (
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-500">Free trial</span>
                          <span className="text-sm font-semibold text-gray-900">{plan.trialDays} days</span>
                        </div>
                      )}

                      {/* Meter rows (parked) */}
                      {METERS_ENABLED && meter && (
                        <div className="mt-2 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">Included {meter.label}</span>
                            <TextField
                              type="text"
                              inputMode="numeric"
                              value={localMeterInputs[`${plan.id}-${meter.id}-includedQty`] ?? '0'}
                              onChange={(e) => setLocalMeterInputs((prev) => ({ ...prev, [`${plan.id}-${meter.id}-includedQty`]: e.target.value }))}
                              onBlur={(e) => updatePlanMeter(plan.id, meter.id, 'includedQty', e.target.value)}
                              className="w-24 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-right tabular-nums focus:border-kv-agentic-blue focus:outline-none"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">Overage per {meter.unit}</span>
                            <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1">
                              <span className="text-xs text-gray-400">$</span>
                              <TextField tone="transparent"
                                type="text"
                                inputMode="decimal"
                                value={localMeterInputs[`${plan.id}-${meter.id}-overageMinor`] ?? '0.00'}
                                onChange={(e) => setLocalMeterInputs((prev) => ({ ...prev, [`${plan.id}-${meter.id}-overageMinor`]: e.target.value }))}
                                onBlur={(e) => updatePlanMeter(plan.id, meter.id, 'overageMinor', e.target.value)}
                                className="w-16 text-sm text-right tabular-nums focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Features section — flex-1 so all cards in the row share equal height */}
                    <div className="border-t border-gray-100 px-5 pt-3 pb-2 flex flex-col flex-1 min-h-[80px]">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2.5 shrink-0">
                        Limits and Features
                      </p>
                      {items.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No features — click Edit to add.</p>
                      ) : (
                        <div
                          className="overflow-y-auto max-h-52 space-y-2.5 pr-0.5"
                          style={{ scrollbarWidth: 'thin', scrollbarColor: `${cssColor('d1d5db')} transparent` }}
                        >
                          {items.map((item) => {
                            const planIntervals = Object.keys(item.values);
                            const same = planIntervals.every((i) => JSON.stringify(item.values[i]) === JSON.stringify(item.values[planIntervals[0]]));
                            return (
                              <div key={item.featureId} className="flex items-center justify-between gap-3">
                                <span className="text-sm text-gray-600">{item.feature.name}</span>
                                {same ? (
                                  displayValue(item.feature, item.values[planIntervals[0]])
                                ) : (
                                  // Different per billing period: show each.
                                  <div className="flex flex-col items-end gap-1">
                                    {planIntervals.map((interval) => (
                                      <div key={interval} className="flex items-center gap-2">
                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                          {INTERVAL_SHORT[interval] ?? interval}
                                        </span>
                                        {displayValue(item.feature, item.values[interval])}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Fixed footer: analytics + actions */}
                    <div className="shrink-0 px-5 pt-3 pb-4 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl">
                      {METERS_ENABLED && (
                      <div className="space-y-1 pb-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Your cost at full usage</span>
                          <span className="font-medium text-gray-700">${costAtFull.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Plan margin</span>
                          <span className={`font-semibold ${marginColor}`}>
                            {margin !== null ? `${margin}% · ` : ''}{marginLabel}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Overage margin</span>
                          <span className="font-medium text-gray-700">
                            {overageMargin !== null ? `${overageMargin}%` : '—'}
                          </span>
                        </div>
                      </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <Button tone="primary-gradient"
                          onClick={() => openEdit(plan)}
                          className="flex-1 rounded-xl px-3 py-1.5 text-xs font-medium transition-all"
                        >
                          Edit
                        </Button>
                        <Button tone="custom"
                          onClick={() => toggleArchived(plan)}
                          className="flex-1 rounded-xl px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-all"
                          title={plan.isActive === false ? 'Offer this plan to new subscribers again' : 'Stop offering this plan; existing subscribers keep it'}
                        >
                          {plan.isActive === false ? 'Restore' : 'Archive'}
                        </Button>
                        <Button tone="custom"
                          onClick={() => handleDelete(plan)}
                          className="flex-1 rounded-xl px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-all"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* When a customer goes over (parked with usage meters) */}
            {METERS_ENABLED && (
            <Surface tone="card" className="rounded-2xl border p-5">
              <p className="text-sm font-semibold text-gray-800 mb-4">When a customer goes over</p>
              <div className="grid grid-cols-3 gap-3">
                {OVERAGE_POLICIES.map((policy) => {
                  const selected = meterPolicy === policy.value;
                  return (
                    <Button tone="custom"
                      key={policy.value}
                      type="button"
                      onClick={async () => {
                        setMeterPolicy(policy.value as any);
                        // Persist immediately only if meter is already saved in DB
                        if (savedMeter && meterLabel) {
                          const costMinor = Math.round(parseFloat(meterCost) * 100);
                          if (!isNaN(costMinor)) {
                            try {
                              const opt = METER_KEY_OPTIONS.find((o) => o.value === meterKey);
                              await aiPodBillingService.upsertUsageMeter(aiPodId, {
                                key: meterKey,
                                label: meterLabel,
                                unit: opt?.unit ?? meterKey,
                                internalCostMinor: costMinor,
                                displayOrder: 0,
                                overagePolicy: policy.value as 'ALLOW' | 'HARD_STOP' | 'NOTIFY_ONLY',
                              });
                              await loadData();
                            } catch {
                              toast.error('Failed to update overage policy');
                            }
                          }
                        }
                        // If no meter yet, the selection is stored in state and applied on meter Save
                      }}
                      className={`flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all ${
                        selected
                          ? 'border-kv-agentic-blue bg-blue-50/40'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                        selected ? 'border-kv-agentic-blue' : 'border-gray-300'
                      }`}>
                        {selected && <div className="h-2 w-2 rounded-full bg-kv-agentic-blue" />}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{policy.label}</p>
                        <p className="mt-0.5 text-xs text-gray-500">{policy.description}</p>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </Surface>
            )}
          </>
        )}
      </div>

      {showModal && (
        <PlanModal
          plan={editingPlan}
          allFeatures={allFeatures}
          initialItems={editingItems}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};
